// Exchange Rates page
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Stack, Paper, Grid, Divider, TextField, MenuItem,
    IconButton, Tooltip, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/UploadFile';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    TrendChart, fmtDate, CHART_COLORS,
} from '../agents/AgentUIKit';
import {
    listExchangeRates, createRate, deleteRate, bulkImportRates,
    latestRate, rateSummary,
} from './api';

const RATE_TYPES = ['SPOT', 'AVERAGE', 'CLOSING', 'HISTORICAL', 'BUDGETED'];
const SOURCES = ['MANUAL', 'ECB', 'FED', 'OPENEXCHANGERATES', 'IMPORTED'];

export default function ExchangeRates() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rates, setRates] = useState([]);
    const [summary, setSummary] = useState(null);
    const [lookupResult, setLookupResult] = useState(null);

    // Filters
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');
    const [filterType, setFilterType] = useState('');

    // New rate form
    const [form, setForm] = useState({
        from_currency: 'USD', to_currency: 'EUR',
        rate: '', rate_date: new Date().toISOString().slice(0, 10),
        rate_type: 'SPOT', source: 'MANUAL',
    });

    // Lookup form
    const [lookupForm, setLookupForm] = useState({ from_currency: 'USD', to_currency: 'EUR', as_of: '' });

    // Bulk import
    const [bulkJson, setBulkJson] = useState('');

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadRates = async () => {
        const params = {};
        if (filterFrom) params.from_currency = filterFrom;
        if (filterTo) params.to_currency = filterTo;
        if (filterType) params.rate_type = filterType;
        const r = await run(() => listExchangeRates(params));
        if (r?.data?.success) setRates(r.data.data);
    };

    const loadSummary = async () => {
        const r = await run(rateSummary);
        if (r?.data?.success) setSummary(r.data.data);
    };

    useEffect(() => { loadSummary(); loadRates(); }, []);  // eslint-disable-line

    const addRate = async () => {
        if (!form.from_currency || !form.to_currency || !form.rate) return;
        const r = await run(() => createRate({ ...form, rate: parseFloat(form.rate) }));
        if (r?.data?.success) {
            setForm({ ...form, rate: '' });
            await loadRates();
            await loadSummary();
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this rate?')) return;
        const r = await run(() => deleteRate(id));
        if (r?.data?.success) { await loadRates(); await loadSummary(); }
    };

    const lookupLatest = async () => {
        if (!lookupForm.from_currency || !lookupForm.to_currency) return;
        const params = {
            from_currency: lookupForm.from_currency,
            to_currency: lookupForm.to_currency,
        };
        if (lookupForm.as_of) params.as_of = lookupForm.as_of;
        const r = await run(() => latestRate(params));
        if (r?.data?.success) setLookupResult(r.data.data);
        else setLookupResult({ error: r?.data?.message || 'Not found' });
    };

    const doBulkImport = async () => {
        let parsed;
        try { parsed = JSON.parse(bulkJson); }
        catch { setError('Invalid JSON'); return; }
        if (!Array.isArray(parsed)) { setError('Expected an array of rates'); return; }
        const r = await run(() => bulkImportRates(parsed));
        if (r?.data?.success) {
            setBulkJson('');
            await loadRates();
            await loadSummary();
        }
    };

    // Chart: trend for filtered pair
    const trendData = rates
        .filter(r => r.rate_type === 'SPOT' || rates.length < 30)
        .sort((a, b) => a.rate_date.localeCompare(b.rate_date))
        .slice(-60)
        .map(r => ({ date: r.rate_date, rate: r.rate }));

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight={700}>Exchange Rates</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Multi-Currency — rate feeds, manual overrides, lookup & bulk import
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => { loadSummary(); loadRates(); }}><RefreshIcon /></IconButton>
                </Stack>
                {error && <Typography variant="caption" color="error">{error}</Typography>}

                <SummaryGrid cards={[
                    { label: 'Total Rates', value: summary?.total_rates ?? '—', accent: 'primary' },
                    { label: 'Currency Pairs', value: summary?.currency_pairs ?? '—', accent: 'info' },
                    { label: 'Latest Rate Date', value: fmtDate(summary?.latest_rate_date) || '—', accent: 'secondary' },
                    { label: 'Rate Age (days)', value: summary?.latest_rate_age_days ?? '—', accent: (summary?.latest_rate_age_days ?? 0) > 7 ? 'warning' : 'success' },
                ]} />

                {/* Add Rate */}
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <SectionHeader title="Add Rate" subtitle="Manual rate entry or override" icon={<AddIcon fontSize="small" />} />
                    <Grid container spacing={1}>
                        <Grid item xs={6} md={1.5}><TextField size="small" fullWidth label="From" value={form.from_currency} onChange={e => setForm({ ...form, from_currency: e.target.value.toUpperCase().slice(0, 3) })} /></Grid>
                        <Grid item xs={6} md={1.5}><TextField size="small" fullWidth label="To" value={form.to_currency} onChange={e => setForm({ ...form, to_currency: e.target.value.toUpperCase().slice(0, 3) })} /></Grid>
                        <Grid item xs={6} md={2}><TextField size="small" fullWidth type="number" label="Rate" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} /></Grid>
                        <Grid item xs={6} md={2}><TextField size="small" fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.rate_date} onChange={e => setForm({ ...form, rate_date: e.target.value })} /></Grid>
                        <Grid item xs={6} md={1.5}>
                            <TextField size="small" fullWidth select label="Type" value={form.rate_type} onChange={e => setForm({ ...form, rate_type: e.target.value })}>
                                {RATE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                            <TextField size="small" fullWidth select label="Source" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                                {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button variant="contained" size="small" fullWidth startIcon={<AddIcon />} onClick={addRate} disabled={loading || !form.rate}>
                                Add Rate
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Lookup */}
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <SectionHeader title="Rate Lookup" subtitle="Get latest rate for a currency pair as of a date" />
                    <Grid container spacing={1}>
                        <Grid item xs={6} md={2}><TextField size="small" fullWidth label="From" value={lookupForm.from_currency} onChange={e => setLookupForm({ ...lookupForm, from_currency: e.target.value.toUpperCase().slice(0, 3) })} /></Grid>
                        <Grid item xs={6} md={2}><TextField size="small" fullWidth label="To" value={lookupForm.to_currency} onChange={e => setLookupForm({ ...lookupForm, to_currency: e.target.value.toUpperCase().slice(0, 3) })} /></Grid>
                        <Grid item xs={6} md={3}><TextField size="small" fullWidth type="date" label="As Of (optional)" InputLabelProps={{ shrink: true }} value={lookupForm.as_of} onChange={e => setLookupForm({ ...lookupForm, as_of: e.target.value })} /></Grid>
                        <Grid item xs={6} md={2}><Button variant="contained" size="small" fullWidth onClick={lookupLatest}>Lookup</Button></Grid>
                        <Grid item xs={12} md={3}>
                            {lookupResult && !lookupResult.error && (
                                <Chip size="small" color="success"
                                    label={`1 ${lookupResult.from_currency} = ${lookupResult.rate} ${lookupResult.to_currency} (${lookupResult.rate_date})`} />
                            )}
                            {lookupResult?.error && <Chip size="small" color="warning" label={lookupResult.error} />}
                        </Grid>
                    </Grid>
                </Paper>

                {/* Filter + List */}
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <SectionHeader
                        title="All Rates"
                        actions={
                            <Stack direction="row" spacing={0.5}>
                                <TextField size="small" placeholder="From" value={filterFrom} onChange={e => setFilterFrom(e.target.value.toUpperCase().slice(0, 3))} sx={{ width: 80 }} />
                                <TextField size="small" placeholder="To" value={filterTo} onChange={e => setFilterTo(e.target.value.toUpperCase().slice(0, 3))} sx={{ width: 80 }} />
                                <TextField size="small" select value={filterType} onChange={e => setFilterType(e.target.value)} sx={{ width: 120 }}>
                                    <MenuItem value="">All types</MenuItem>
                                    {RATE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </TextField>
                                <Button size="small" variant="outlined" onClick={loadRates}>Apply</Button>
                            </Stack>
                        }
                    />
                    {trendData.length > 1 && filterFrom && filterTo && (
                        <TrendChart
                            data={trendData}
                            xKey="date"
                            series={[{ key: 'rate', label: `${filterFrom}/${filterTo}`, color: CHART_COLORS.primary }]}
                            height={180}
                            type="area"
                            showLegend={false}
                        />
                    )}
                    {loading && !rates.length ? <LoadingBlock rows={4} /> : (
                        <DrillTable
                            columns={[
                                { key: 'rate_date', label: 'Date' },
                                { key: 'from_currency', label: 'From' },
                                { key: 'to_currency', label: 'To' },
                                { key: 'rate', label: 'Rate', align: 'right', render: r => r.rate?.toFixed(6) },
                                { key: 'rate_type', label: 'Type', render: r => <Chip size="small" label={r.rate_type} variant="outlined" /> },
                                { key: 'source', label: 'Source' },
                                { key: 'is_override', label: 'Override', render: r => r.is_override && <Chip size="small" label="Manual" color="warning" /> },
                                { key: 'actions', label: '', render: r => (
                                    <Tooltip title="Delete">
                                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                )},
                            ]}
                            rows={rates}
                            maxHeight={420}
                        />
                    )}
                </Paper>

                {/* Bulk Import */}
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <SectionHeader title="Bulk Import" subtitle='Array of {from_currency, to_currency, rate, rate_date, rate_type, source}' icon={<UploadIcon fontSize="small" />} />
                    <TextField
                        multiline rows={4} fullWidth size="small"
                        placeholder='[{"from_currency":"USD","to_currency":"EUR","rate":0.92,"rate_date":"2026-04-24","rate_type":"SPOT","source":"ECB"}]'
                        value={bulkJson} onChange={e => setBulkJson(e.target.value)}
                    />
                    <Box sx={{ mt: 1 }}>
                        <Button size="small" variant="contained" disabled={!bulkJson.trim() || loading} onClick={doBulkImport} startIcon={<UploadIcon />}>
                            Import
                        </Button>
                    </Box>
                </Paper>
            </Stack>
        </Box>
    );
}
